import { DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES } from '@/lib/constants/pagination';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { getDepartments } from '@/modules/settings/api/departments.api';
import { getRoles } from '@/modules/settings/api/roles.api';
import { getSeniorities } from '@/modules/settings/api/seniorities.api';

import { makeCityOptionId } from '../utils/city-option';
import { getTeamLocationsStructured } from './location.api';

/**
 * Loads departments for filter dropdown with pagination support.
 * Filters by search string and returns paginated results.
 */
export const loadDepartmentsForFilter = async (params: {
  searchString?: string;
  cursor?: number | null;
}): Promise<PaginatedResponse<DropdownSearchItem>> => {
  // Fetch all departments (API doesn't support pagination yet)
  const allDepartments = await getDepartments();

  // Filter by search string if provided
  const filtered = params.searchString
    ? allDepartments.filter(dept =>
        dept.name.toLowerCase().includes(params.searchString?.toLowerCase() ?? '')
      )
    : allDepartments;

  // Apply pagination
  const pageSize = DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES;
  const startIndex = params.cursor || 0;
  const endIndex = startIndex + pageSize;
  const paginatedItems = filtered.slice(startIndex, endIndex);

  return new PaginatedResponse({
    items: paginatedItems.map(dept => ({
      id: String(dept.id),
      name: dept.name,
    })),
    pagination: {
      total: filtered.length,
      cursor: endIndex < filtered.length ? endIndex : null,
    },
  });
};

/**
 * Loads roles for filter dropdown with pagination support.
 * Filters by search string and returns paginated results.
 */
export const loadRolesForFilter = async (params: {
  searchString?: string;
  cursor?: number | null;
}): Promise<PaginatedResponse<DropdownSearchItem>> => {
  // Fetch all roles (API doesn't support pagination yet)
  const allRoles = await getRoles();

  // Filter by search string if provided
  const filtered = params.searchString
    ? allRoles.filter(role =>
        role.name.toLowerCase().includes(params.searchString?.toLowerCase() ?? '')
      )
    : allRoles;

  // Apply pagination
  const pageSize = DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES;
  const startIndex = params.cursor || 0;
  const endIndex = startIndex + pageSize;
  const paginatedItems = filtered.slice(startIndex, endIndex);

  return new PaginatedResponse({
    items: paginatedItems.map(role => ({
      id: String(role.id),
      name: role.name,
    })),
    pagination: {
      total: filtered.length,
      cursor: endIndex < filtered.length ? endIndex : null,
    },
  });
};

/**
 * Loads seniorities for filter dropdown with pagination support.
 * Filters by search string and returns paginated results.
 */
export const loadSenioritiesForFilter = async (params: {
  searchString?: string;
  cursor?: number | null;
}): Promise<PaginatedResponse<DropdownSearchItem>> => {
  // Fetch all seniorities (API doesn't support pagination yet)
  const allSeniorities = await getSeniorities();

  // Filter by search string if provided
  const filtered = params.searchString
    ? allSeniorities.filter(seniority =>
        seniority.name.toLowerCase().includes(params.searchString?.toLowerCase() ?? '')
      )
    : allSeniorities;

  // Apply pagination
  const pageSize = DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES;
  const startIndex = params.cursor || 0;
  const endIndex = startIndex + pageSize;
  const paginatedItems = filtered.slice(startIndex, endIndex);

  return new PaginatedResponse({
    items: paginatedItems.map(seniority => ({
      id: String(seniority.id),
      name: seniority.name,
    })),
    pagination: {
      total: filtered.length,
      cursor: endIndex < filtered.length ? endIndex : null,
    },
  });
};

/**
 * Loads countries for filter dropdown with pagination support.
 * Returns unique countries from team member locations.
 */
export const loadCountriesForFilter = async (params: {
  searchString?: string;
  cursor?: number | null;
}): Promise<PaginatedResponse<DropdownSearchItem>> => {
  // Fetch all structured locations from API
  const structuredLocations = await getTeamLocationsStructured();

  // Extract unique countries
  const allCountries = structuredLocations.map(location => location.country);

  // Filter by search string if provided
  const filtered = params.searchString
    ? allCountries.filter(country =>
        country.toLowerCase().includes(params.searchString?.toLowerCase() ?? '')
      )
    : allCountries;

  // Apply pagination
  const pageSize = DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES;
  const startIndex = params.cursor || 0;
  const endIndex = startIndex + pageSize;
  const paginatedItems = filtered.slice(startIndex, endIndex);

  return new PaginatedResponse({
    items: paginatedItems.map(country => ({
      id: country,
      name: country,
    })),
    pagination: {
      total: filtered.length,
      cursor: endIndex < filtered.length ? endIndex : null,
    },
  });
};

/**
 * Loads cities for filter dropdown with pagination support.
 * Optionally filters by selected country and/or search string.
 * Each city value is encoded as "country|city" to preserve the country relationship.
 */
export const loadCitiesForFilter = async (params: {
  searchString?: string;
  country?: string | string[];
  cursor?: number | null;
}): Promise<PaginatedResponse<DropdownSearchItem>> => {
  // Fetch all structured locations from API
  const structuredLocations = await getTeamLocationsStructured();

  // Extract all cities with their country information
  const allCities: Array<{ country: string; city: string }> = [];

  for (const location of structuredLocations) {
    // Filter by country if provided
    const countryFilter = params.country
      ? Array.isArray(params.country)
        ? params.country.includes(location.country)
        : params.country === location.country
      : true;

    if (!countryFilter) {
      continue;
    }

    // Add all cities from this country with country information
    if (location.cities && location.cities.length > 0) {
      location.cities.forEach(city => {
        allCities.push({ country: location.country, city });
      });
    }
  }

  // Filter by search string if provided (search in city names)
  const filtered = params.searchString
    ? allCities.filter(item =>
        item.city.toLowerCase().includes(params.searchString?.toLowerCase() ?? '')
      )
    : allCities;

  // Apply pagination
  const pageSize = DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES;
  const startIndex = params.cursor || 0;
  const endIndex = startIndex + pageSize;
  const paginatedItems = filtered.slice(startIndex, endIndex);

  return new PaginatedResponse({
    items: paginatedItems.map(item => ({
      id: makeCityOptionId(item.country, item.city), // Encode country and city in the value
      name: item.city, // Display only city name in the UI
    })),
    pagination: {
      total: filtered.length,
      cursor: endIndex < filtered.length ? endIndex : null,
    },
  });
};
